import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  IGenericServiceResponse,
  IServiceResponse,
} from '../interfaces/IServiceResponse';
import { environment } from 'src/environments/environment';
import { EpisodeOfCareService } from './episodeOfCare.service';
import { PatientService } from './patient.service';
import { IVitalSignDetailed } from '../interfaces/VitalSign/IVitalSignDetailed';
import { IAddVitalSign } from '../interfaces/VitalSign/IAddVitalSign';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from './swal.service';

@Injectable({ providedIn: 'root' })
export class VitalSignService {
  public vitalSignModalSub = new BehaviorSubject<boolean>(false);

  public vitalSignEditSub = new BehaviorSubject<IVitalSignDetailed | undefined>(
    undefined
  );

  public vitalSignDataSub = new BehaviorSubject<IVitalSignDetailed[]>([]);

  public vitalSignLoadingSub = new BehaviorSubject<boolean>(false);
  public vitalSignLoadingSub$ = this.vitalSignLoadingSub.asObservable();

  constructor(
    private readonly _http: HttpClient,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  async getData() {
    this.vitalSignLoadingSub.next(true);
    this.getVitalSign(
      this.patientService.patientIDsignal(),
      this.eocService.currentEocId(),
      this.eocService.currentVisitId()
    ).subscribe({
      next: (response) => {
        this.vitalSignDataSub.next(
          response.data.map((item) => this.enrichVitalSignForTable(item))
        );
        this.vitalSignLoadingSub.next(false);
      },
      error: (err) => {
        this.swalToastService.toastError('Unable to retrieve vital signs');
        console.error('Error fetching vital signs:', err);
      },
    });
  }

  // Adds the MEWS Score row the Vital Signs table displays, since the
  // backend only sends the raw physiological values it is derived from.
  // The AVPU (level of consciousness) parameter of the standard MEWS chart
  // is intentionally left out: this system does not capture it as a vital sign.
  private enrichVitalSignForTable(
    item: IVitalSignDetailed
  ): IVitalSignDetailed {
    const mews = this.calculateMews(item);

    if (!mews) {
      return { ...item, mewsScore: undefined, mewsScoreLabel: undefined };
    }

    const scoreText = `${mews.score}`;

    return {
      ...item,
      mewsScore: mews.score,
      mewsScoreLabel: scoreText,
      __badge__mewsScoreLabel: {
        text: scoreText,
        className: this.getMewsBadgeClassName(mews.riskLevel),
        title: this.getMewsTooltip(mews.score, mews.hasCriticalParameter),
      },
    };
  }

  calculateMews(vitalSign: IVitalSignDetailed):
    | { score: number; riskLevel: string; hasCriticalParameter: boolean }
    | undefined {
    const { temperature, respiratoryRate, heartRate, systolicBloodPressure } =
      vitalSign;

    if (
      !this.isFiniteNumber(temperature) ||
      !this.isFiniteNumber(respiratoryRate) ||
      !this.isFiniteNumber(heartRate) ||
      !this.isFiniteNumber(systolicBloodPressure)
    ) {
      return undefined;
    }

    const temperatureScore = this.scoreTemperature(temperature);
    const respiratoryRateScore = this.scoreRespiratoryRate(respiratoryRate);
    const heartRateScore = this.scoreHeartRate(heartRate);
    const systolicBloodPressureScore =
      this.scoreSystolicBloodPressure(systolicBloodPressure);

    const score =
      temperatureScore +
      respiratoryRateScore +
      heartRateScore +
      systolicBloodPressureScore;

    // Any single parameter scoring the maximum of 3 escalates to High risk
    // regardless of the total (temperature can only ever score up to 2).
    const hasCriticalParameter =
      respiratoryRateScore === 3 ||
      heartRateScore === 3 ||
      systolicBloodPressureScore === 3;

    return {
      score,
      riskLevel: this.getMewsRiskLevel(score, hasCriticalParameter),
      hasCriticalParameter,
    };
  }

  private getMewsRiskLevel(
    score: number,
    hasCriticalParameter: boolean
  ): string {
    if (hasCriticalParameter || score >= 5) return 'High';
    if (score === 4) return 'Medium';
    if (score >= 2) return 'Low-Medium';
    return 'Low';
  }

  getMewsBadgeClassName(riskLevel: string): string {
    switch (riskLevel) {
      case 'High':
        return 'mews-score-high';
      case 'Medium':
        return 'mews-score-medium';
      case 'Low-Medium':
        return 'mews-score-low-medium';
      default:
        return 'mews-score-low';
    }
  }

  // Percentage bands are the actual combined-endpoint figures (death, cardiac
  // arrest team call, HDU or ICU admission within 60 days) reported by
  // Subbe et al. 2001 for ScoreMax 0-2 / 3-4 / 5-9; they don't line up with
  // the four risk-level bands above, which follow this system's own
  // escalation policy rather than the study's bands.
  getMewsTooltip(score: number, hasCriticalParameter: boolean): string {
    const percent = score <= 2 ? '7.9%' : score <= 4 ? '12.7%' : '30%';
    const base = this.translate.instant('MEWS 60-day risk', { percent });

    return hasCriticalParameter
      ? `${base} ${this.translate.instant('MEWS single parameter warning')}`
      : base;
  }

  private isFiniteNumber(value?: number | null): value is number {
    return typeof value === 'number' && Number.isFinite(value);
  }

  private scoreTemperature(value: number): number {
    return value < 35 || value >= 38.5 ? 2 : 0;
  }

  private scoreRespiratoryRate(value: number): number {
    if (value < 9) return 2;
    if (value <= 14) return 0;
    if (value <= 20) return 1;
    if (value <= 29) return 2;
    return 3;
  }

  private scoreHeartRate(value: number): number {
    if (value < 40) return 2;
    if (value <= 50) return 1;
    if (value <= 100) return 0;
    if (value <= 110) return 1;
    if (value <= 129) return 2;
    return 3;
  }

  private scoreSystolicBloodPressure(value: number): number {
    if (value <= 70) return 3;
    if (value <= 80) return 2;
    if (value <= 100) return 1;
    if (value <= 199) return 0;
    return 2;
  }

  getVitalSign(
    patientId: string,
    episodeCareId: string,
    visitId: string
  ): Observable<IGenericServiceResponse<IVitalSignDetailed[]>> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this._http.get<IGenericServiceResponse<IVitalSignDetailed[]>>(
      `${environment.BACKEND_URL}VitalSign/${patientId}/${episodeCareId}?visitId=${visitId}`,
      {
        headers: headers,
      }
    );
  }

  addVitalSign(vitalSignObj: IAddVitalSign) {
    vitalSignObj.visitId = this.eocService.currentVisitId();
    vitalSignObj.patientId = this.patientService.patientIDsignal();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.post(
      `${environment.BACKEND_URL}VitalSign`,
      vitalSignObj,
      {
        headers: headers,
      }
    );
  }

  editVitalSign(vitalSignObj: IAddVitalSign) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.put(
      `${environment.BACKEND_URL}VitalSign/${vitalSignObj.id}`,
      vitalSignObj,
      {
        headers: headers,
      }
    );
  }

  deleteVitalSign(vitalSignId: string): Observable<IServiceResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this._http.delete<IServiceResponse>(
      `${environment.BACKEND_URL}VitalSign/${vitalSignId}`,
      {
        headers: headers,
      }
    );
  }

  deleteVitalSignFromTable(vitalSignId: string) {
    // REMOVE THE Vaccination
    this.vitalSignDataSub.next(
      this.vitalSignDataSub.value.filter((v) => v.id?.toString() != vitalSignId)
    );
  }
}
